import WorkspaceSecondaryButton from "@/components/SecondaryButton";
import { useEffect } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useClassrooms } from "@/hooks/useClassrooms";
import ClassroomCard from "./ClassroomCard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import AnalyticsLinkButton from "@/components/AnalyticsLinkButton";
import { buildGroupAnalyticsHref } from "@/features/analytics/service/deepLink";
import { debugLog } from "@/utils/debugLog";
import { useMyTrial } from "@/hooks/useMyTrial";
import { isTrialLocked } from "@/components/TrialBanner";

const TeacherClassroomsView = () => {
  // Shared with the workspace shell, which names the open group in the
  // sidebar; revalidated here so the list is fresh whenever this page opens.
  const {
    classrooms: loadedClassrooms,
    error: loadError,
    reload,
  } = useClassrooms({ shouldRevalidate: true });
  const classrooms = loadedClassrooms ?? [];
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const selected = classrooms.find((c) => String(c.id) === search.get("group"));
  const openGroup = (id?: number) => {
    setSearch((previous) => {
      const next = new URLSearchParams(previous);
      if (id == null) next.delete("group");
      else next.set("group", String(id));
      return next;
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const { trial } = useMyTrial();
  const locked = trial ? isTrialLocked(trial) : false;
  useEffect(() => {
    if (loadedClassrooms) debugLog("Teacher classrooms:", loadedClassrooms);
  }, [loadedClassrooms]);

  // A failed refresh keeps the list already on screen.
  if (loadedClassrooms === null && loadError) {
    return (
      <div role="alert" className="et-group-error">
        <p>Failed to load groups. Please try again.</p>
        <WorkspaceSecondaryButton
          onClick={reload}
          className="et-secondary-button"
        >
          Try Again
        </WorkspaceSecondaryButton>
      </div>
    );
  }

  if (loadedClassrooms === null) {
    return <LoadingSpinner message="Loading groups..." />;
  }

  return (
    <div className="et-groups-view">
      <div className="flex flex-col gap-4">
        <div className="et-groups-heading">
          <h1>{selected ? selected.name : "All your Groups"}</h1>
          <div className="flex flex-wrap justify-end gap-3">
            {selected && (
              <AnalyticsLinkButton
                className="et-secondary-button"
                href={buildGroupAnalyticsHref({
                  classroomId: selected.id,
                  classroomName: selected.name,
                  teacherUserId: selected.teacher_user_id,
                })}
                subject={selected.name}
              />
            )}
            {!selected && (
              <WorkspaceSecondaryButton
                className="et-secondary-button"
                onClick={() => navigate("/analytics")}
              >
                View All Analytics <ArrowUpRight size={15} />
              </WorkspaceSecondaryButton>
            )}
          </div>
        </div>
      </div>

      {selected && (
        <button className="et-group-back" onClick={() => openGroup()}>
          <ArrowLeft size={16} /> All your Groups
        </button>
      )}
      <div
        className={
          locked ? "pointer-events-none select-none opacity-60 blur-[2px]" : ""
        }
      >
        {classrooms.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No groups created yet
              </h3>
              <p className="text-gray-500">
                Create your first group to start organizing your lessons and
                students.
              </p>
            </div>
          </div>
        ) : (
          <div className={selected ? "et-group-detail" : "et-groups-grid"}>
            {(selected ? [selected] : classrooms).map((classroom, index) => (
              <ClassroomCard
                key={classroom.id}
                classroom={classroom}
                index={index}
                onOpen={selected ? undefined : () => openGroup(classroom.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherClassroomsView;
